import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCookieConsent } from "../context/CookieConsentContext";

const CookieSettingsModal = () => {
  const { t } = useTranslation();
  const { isModalOpen, closeSettings, preferences, saveCustomPreferences } = useCookieConsent();
  const [localPreferences, setLocalPreferences] = useState({
    analytics: preferences.analytics,
    marketing: preferences.marketing,
  });

  useEffect(() => {
    if (isModalOpen) {
      setLocalPreferences({
        analytics: preferences.analytics,
        marketing: preferences.marketing,
      });
    }
  }, [isModalOpen, preferences.analytics, preferences.marketing]);

  if (!isModalOpen) {
    return null;
  }

  const handleToggle = (key) => {
    setLocalPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = () => {
    saveCustomPreferences(localPreferences);
    closeSettings();
  };

  const categories = [
    {
      key: "necessary",
      title: t("cookieSettings.categories.necessary.title"),
      description: t("cookieSettings.categories.necessary.description"),
      value: true,
      locked: true,
    },
    {
      key: "analytics",
      title: t("cookieSettings.categories.analytics.title"),
      description: t("cookieSettings.categories.analytics.description"),
      value: localPreferences.analytics,
    },
    {
      key: "marketing",
      title: t("cookieSettings.categories.marketing.title"),
      description: t("cookieSettings.categories.marketing.description"),
      value: localPreferences.marketing,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={closeSettings} />
      <div className="relative z-10 w-full max-w-xl rounded-3xl border border-white/10 bg-slate-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-300">
              {t("cookieSettings.title")}
            </p>
            <p className="mt-2 text-sm text-gray-200">{t("cookieSettings.description")}</p>
          </div>
          <button
            type="button"
            aria-label={t("cookieSettings.close")}
            onClick={closeSettings}
            className="rounded-full border border-white/20 px-3 py-1 text-xl leading-none text-gray-300 transition hover:bg-white/10"
          >
            &times;
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {categories.map((category) => (
            <div
              key={category.key}
              className="rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:border-cyan-400/60"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-white">{category.title}</p>
                  <p className="mt-1 text-sm text-gray-300">{category.description}</p>
                </div>
                <label className="inline-flex items-center gap-2 text-gray-200">
                  <span className="text-xs uppercase tracking-widest text-gray-400">
                    {category.value ? t("cookieSettings.status.enabled") : t("cookieSettings.status.disabled")}
                  </span>
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-gray-400 text-cyan-400 focus:ring-cyan-400"
                    checked={category.value}
                    disabled={category.locked}
                    onChange={() => (!category.locked ? handleToggle(category.key) : null)}
                  />
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={closeSettings}
            className="rounded-xl border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            {t("cookieSettings.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-xl bg-cyan-400 px-6 py-3 text-sm font-semibold text-slate-900 transition hover:bg-cyan-300"
          >
            {t("cookieSettings.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
